import React from "react";
import { getDaysInMonth } from "./datashow";


const BioMedicalTable = ({
  biomedicalData,
  loading,
  selectedMonth,
  getLocationData,
  sourceLabels,
  sourceIds,
  selectedSourceIds,
  financialYear,
  financialYearId,
}) => {
  // Bio Medical Waste categories with enhanced colors
  const bioMedicalCategories = [
    { key: "yellow", name: "Yellow", color: "#FFD700", bgColor: "#FFF9C4" },
    { key: "red", name: "Red", color: "#F44336", bgColor: "#FFEBEE" },
    { key: "white", name: "White", color: "#9E9E9E", bgColor: "#F5F5F5" },
    { key: "blue", name: "Blue", color: "#2196F3", bgColor: "#E3F2FD" },
    { key: "cytotoxic", name: "Cytotoxic", color: "#9C27B0", bgColor: "#F3E5F5" },
  ];


  // Format number: remove trailing zeros after decimal
  const formatNum = (val) => {
    if (val === null || val === undefined || val === "" || val === "Submission Pending") return val;
    const num = typeof val === "number" ? val : parseFloat(val);
    if (isNaN(num)) return val;
    return parseFloat(num.toFixed(2)).toString();
  };

  // Biomedical data functions
  const getBiomedicalValue = (sourceId, wasteTypeIndex, valueType) => {
    const sourceData = biomedicalData.find(
      (item) => item.sourceId === sourceId && item.fromDate == selectedMonth
    );
    if (!sourceData) return "Submission Pending";

    try {
      let value;
      switch (valueType) {
        case "actual":
          value = sourceData.answer?.[0]?.[wasteTypeIndex];
          break;
        case "min":
          value = sourceData.minTarget?.[0]?.[wasteTypeIndex];
          break;
        case "max":
          value = sourceData.maxTarget?.[0]?.[wasteTypeIndex];
          break;
        default:
          return "Submission Pending";
      }

      return value !== undefined && value !== null
        ? value
        : "Submission Pending";
    } catch (error) {
      return "Submission Pending";
    }
  };

  // Get intensity data for a specific source
  const getIntensityData = (sourceId) => {
    const sourceData = biomedicalData.find(
      (item) => item.sourceId === sourceId && item.fromDate == selectedMonth
    );
    return sourceData?.intensityData || [];
  };

  // Calculate total waste for all categories
  const calculateTotalWaste = (sourceId, valueType) => {
    let total = 0;
    let hasValidData = false;
    let allPending = true;

    for (let i = 0; i < bioMedicalCategories.length; i++) {
      const value = getBiomedicalValue(sourceId, i, valueType);
      
      if (value !== "Submission Pending") {
        allPending = false;
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
          total += numValue;
          hasValidData = true;
        }
      }
    }

    if (allPending) return "Submission Pending";
    if (!hasValidData) return "Submission Pending";
    
    return total;
  };

  const calculatePerBedPerDay = (sourceId, approvedBeds) => {
    if (!approvedBeds) return "Submission Pending";

    const beds = parseFloat(approvedBeds);
    if (isNaN(beds) || beds === 0) return "Submission Pending";

    const totalMax = calculateTotalWaste(sourceId, "max");
    if (totalMax === "Submission Pending") return "Submission Pending";

    const days = getDaysInMonth(selectedMonth);
    if (!days) return "Submission Pending";

    return (totalMax / (beds * days));
  };


  // Calculate intensity metric (Total Waste / Intensity Answer)
  const calculateIntensityMetric = (sourceId, intensityAnswer, valueType) => {
    const totalWaste = calculateTotalWaste(sourceId, valueType);
    
    if (totalWaste === "Submission Pending" || !intensityAnswer) {
      return "Submission Pending";
    }

    const answerNum = parseFloat(intensityAnswer);
    if (isNaN(answerNum) || answerNum === 0) {
      return "Submission Pending";
    }

    return totalWaste / answerNum;
  };

  const calculateDifference = (sourceId, wasteTypeIndex) => {
    const actual = getBiomedicalValue(sourceId, wasteTypeIndex, "actual");
    const max = getBiomedicalValue(sourceId, wasteTypeIndex, "max");

    if (actual === "Submission Pending" || max === "Submission Pending")
      return "";

    const actualNum = parseFloat(actual);
    const maxNum = parseFloat(max);

    if (isNaN(actualNum) || isNaN(maxNum)) return "";
    return actualNum - maxNum;
  };

  const calculateYellowDifference = (sourceId) => {
    const YELLOW_INDEX = 0;     
    const CYTOTOXIC_INDEX = 4; 

    const actual = getBiomedicalValue(sourceId, YELLOW_INDEX, "actual");
    const max = getBiomedicalValue(sourceId, YELLOW_INDEX, "max");

    if (
      actual === "Submission Pending" ||
      max === "Submission Pending"
    ) {
      return "";
    }

    const cytotoxicRaw = getBiomedicalValue(
      sourceId,
      CYTOTOXIC_INDEX,
      "actual"
    );

    let cytotoxic =
      cytotoxicRaw === "Submission Pending"
        ? 0
        : parseFloat(cytotoxicRaw);

    const actualNum = parseFloat(actual);
    const maxNum = parseFloat(max);

    if (isNaN(actualNum) || isNaN(maxNum)) {
      return "";
    }

    if(isNaN(cytotoxic)){
      cytotoxic = 0
    }
    return actualNum + cytotoxic - (maxNum) ;
  };
  

  // Calculate difference for calculated rows (Total Waste and Intensity metrics)
  const calculateCalculatedDifference = (actualValue, maxValue) => {
    if (actualValue === "Submission Pending" || maxValue === "Submission Pending") {
      return "";
    }

    const actualNum = parseFloat(actualValue);
    const maxNum = parseFloat(maxValue);

    if (isNaN(actualNum) || isNaN(maxNum)) {
      return "";
    }

    return actualNum - maxNum;
  };

  const getRemark = (sourceId, wasteTypeIndex, max) => {
    const actual = getBiomedicalValue(sourceId, wasteTypeIndex, "actual");
    if (actual === "Submission Pending") return "";

    const difference = calculateDifference(sourceId, wasteTypeIndex);
    if (difference === "") return "";
    if (max === 0) return "Within Limit";

    return difference <= 0 ? "Within Limit" : "Exceeding Limit";
  };

  // Get remark for calculated rows
  const getCalculatedRemark = (difference) => {
    if (difference === "" || difference === "Submission Pending") return "";
    return difference <= 0 ? "Within Limit" : "Exceeding Limit";
  };

  // Export function
  const exportToExcel = () => {
    const locationData = getLocationData();
    const exportData = [];

    exportData.push([
      "Location",
      "Waste Type",
      "Min Trigger Value",
      "Max Trigger Value",
      "Actual Value",
      "Difference",
      "Remark",
    ]);

    locationData.forEach((location) => {
      // Regular categories
      bioMedicalCategories.forEach((category, categoryIndex) => {
        const minValue = getBiomedicalValue(location.id, categoryIndex, "min");
        const maxValue = getBiomedicalValue(location.id, categoryIndex, "max");
        const actualValue = getBiomedicalValue(
          location.id,
          categoryIndex,
          "actual"
        );

        const difference =
          category.key === "yellow"
            ? calculateYellowDifference(location.id)
            : calculateDifference(location.id, categoryIndex);

        const remark = getRemark(location.id, categoryIndex);

        exportData.push([
          location.name,
          category.name,
          minValue,
          maxValue,
          actualValue,
          difference === "Submission Pending"
            ? difference
            : formatNum(difference),
          remark,
        ]);
      });

      // Total Waste row
      const totalActual = calculateTotalWaste(location.id, "actual");
      const totalMin = calculateTotalWaste(location.id, "min");
      const totalMax = calculateTotalWaste(location.id, "max");
      const totalDiff = calculateCalculatedDifference(totalActual, totalMax);
      const totalRemark = getCalculatedRemark(totalDiff);

      exportData.push([
        location.name,
        "Total Biomedical Waste",
        totalMin === "Submission Pending" ? totalMin : formatNum(totalMin),
        totalMax === "Submission Pending" ? totalMax : formatNum(totalMax),
        totalActual === "Submission Pending" ? totalActual : formatNum(totalActual),
        totalDiff === "Submission Pending" ? totalDiff : formatNum(totalDiff),
        totalRemark,
      ]);

      const intensityData = getIntensityData(location.id);

      const sourceData = biomedicalData.find(
        (item) => item.sourceId === location.id && item.fromDate == selectedMonth
      );

      const approvedBeds = sourceData?.approvedBeds ?? null;
      const perBedMax = calculatePerBedPerDay(location.id, approvedBeds);

      // Always export per bed per day row when no intensity data
      if (intensityData.length === 0) {
        const days = getDaysInMonth(selectedMonth);
        const beds = parseFloat(approvedBeds);
        const perBedActual =
          totalActual !== "Submission Pending" && !isNaN(beds) && beds > 0 && days
            ? parseFloat(totalActual) / (beds * days)
            : "Submission Pending";

        const perBedDiff =
          typeof perBedActual === "number" && typeof perBedMax === "number"
            ? perBedActual - perBedMax
            : "Submission Pending";

        const perBedRemark =
          perBedDiff === "Submission Pending"
            ? ""
            : perBedMax == 0 || perBedDiff <= 0
            ? "Within Limit"
            : "Exceeding Limit";

        exportData.push([
          location.name,
          "Total Biomedical Waste Per Bed Per Day",
          formatNum(0),
          perBedMax === "Submission Pending" ? perBedMax : formatNum(perBedMax),
          perBedActual === "Submission Pending" ? perBedActual : formatNum(perBedActual),
          perBedDiff === "Submission Pending" ? perBedDiff : formatNum(perBedDiff),
          perBedRemark,
        ]);
      }

      intensityData.forEach((intensity) => {
        const intensityActual = calculateIntensityMetric(
          location.id,
          intensity.answer,
          "actual"
        );

        const intensityMin =
          typeof perBedMax === "number" ? 0 : "Submission Pending";

        const intensityMax = perBedMax;

        const intensityDiff =
          typeof intensityActual === "number" && typeof intensityMax === "number"
            ? intensityActual - intensityMax
            : "Submission Pending";

        const intensityRemark =
          intensityDiff === "Submission Pending"
            ? ""
            : intensityDiff <= 0
              ? "Within Limit"
              : "Exceeding Limit";

        exportData.push([
          location.name,
          `Total Biomedical Waste Per ${intensity.title} Per Day`,
          intensityMin === "Submission Pending" ? intensityMin : formatNum(intensityMin),
          intensityMax === "Submission Pending" ? intensityMax : formatNum(intensityMax),
          intensityActual === "Submission Pending" ? intensityActual : formatNum(intensityActual),
          intensityDiff === "Submission Pending" ? intensityDiff : formatNum(intensityDiff),
          intensityRemark,
        ]);
      });
    });

    const csvContent = exportData
      .map((row) =>
        row
          .map((cell) => {
            if (typeof cell === "string") {
              const trimmed = cell.trim();
              return trimmed.includes(",") ? `"${trimmed}"` : trimmed;
            }
            return cell;
          })
          .join(",")
      )
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);

      const currentDate = new Date().toISOString().split("T")[0];
      const selectedYear = financialYear.find(
        (year) => year.id === financialYearId
      );
      const yearText = selectedYear
        ? selectedYear.financial_year_value
        : "Unknown";
      const locationText =
        selectedSourceIds.length === 0 ||
          selectedSourceIds.length === sourceIds.length
          ? "All_Locations"
          : selectedSourceIds.length === 1
            ? sourceLabels[selectedSourceIds[0]] || "Unknown_Location"
            : "Multiple_Locations";
      const monthText = selectedMonth
        ? selectedMonth.replace("-", "_")
        : "All_Months";

      const filename = `Biomedical_Waste_Report_${yearText}_${locationText}_${monthText}_${currentDate}.csv`;
      link.setAttribute("download", filename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };


  const renderBioMedicalTable = () => {
    const locationData = getLocationData();

    if (loading) {
      return (
        <div
          style={{
            textAlign: "center",
            padding: "60px",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            borderRadius: "12px",
          }}
        >
          <div
            style={{
              width: "50px",
              height: "50px",
              border: "4px solid rgba(255,255,255,0.3)",
              borderTop: "4px solid #fff",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 20px",
            }}
          ></div>
          <div style={{ fontSize: "18px", fontWeight: "500", color: "#fff" }}>
            Loading biomedical data...
          </div>
        </div>
      );
    }

    return (
      <div
        style={{
          overflowX: "auto",
          borderRadius: "12px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
          background: "#fff",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "14px",
          }}
        >
          <thead>
            <tr
              style={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "#fff",
              }}
            >
              <th style={{ padding: "16px 12px", fontWeight: "600", textAlign: "center", minWidth: "50px", borderRight: "1px solid rgba(255,255,255,0.2)" }}>
                #
              </th>
              <th style={{ padding: "16px 12px", fontWeight: "600", textAlign: "left", minWidth: "200px", borderRight: "1px solid rgba(255,255,255,0.2)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>🗂️ Waste Type</div>
              </th>
              <th style={{ padding: "16px 12px", fontWeight: "600", textAlign: "center", minWidth: "130px", borderRight: "1px solid rgba(255,255,255,0.2)" }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
                  <span>📉 Min Trigger Value</span>
                </div>
              </th>
              <th style={{ padding: "16px 12px", fontWeight: "600", textAlign: "center", minWidth: "130px", borderRight: "1px solid rgba(255,255,255,0.2)" }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
                  <span>📈 Max Trigger Value</span>
                </div>
              </th>
              <th style={{ padding: "16px 12px", fontWeight: "600", textAlign: "center", minWidth: "120px", borderRight: "1px solid rgba(255,255,255,0.2)" }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
                  <span>📊 Actual Value</span>
                </div>
              </th>
              <th style={{ padding: "16px 12px", fontWeight: "600", textAlign: "center", minWidth: "110px", borderRight: "1px solid rgba(255,255,255,0.2)" }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
                  <span>⚖️ Difference</span>
                </div>
              </th>
              <th style={{ padding: "16px 12px", fontWeight: "600", textAlign: "center", minWidth: "150px" }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
                  <span>💬 Remark</span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {locationData.map((location, locationIndex) => {
              const intensityData = getIntensityData(location.id);
              const sourceData = biomedicalData.find(
                (item) => item.sourceId === location.id && item.fromDate == selectedMonth
              );

              const approvedBeds = sourceData?.approvedBeds ?? null;
              return (
                <React.Fragment key={location.id}>
                  {sourceIds && sourceIds.length === 1 ? (
                    <></>
                  ) : (
                    <tr>
                      <td
                        colSpan="7"
                        style={{
                          padding: "16px 12px",
                          background: "linear-gradient(135deg, #a8e6cf 0%, #88d8a3 100%)",
                          fontWeight: "700",
                          textAlign: "center",
                          color: "#2d5016",
                          fontSize: "16px",
                          borderTop: locationIndex > 0 ? "3px solid #e0e7ff" : "none",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" }}>
                          📍 {location.name}
                        </div>
                      </td>
                    </tr>
                  )}
                  
                  {/* Regular waste categories */}
                  {bioMedicalCategories.map((category, categoryIndex) => {
                    const max = getBiomedicalValue(location.id, categoryIndex, "max");
                    const difference =
                      category.key === "yellow"
                        ? calculateYellowDifference(location.id)
                        : calculateDifference(location.id, categoryIndex);
                    const remark = getRemark(location.id, categoryIndex, max);
                    return (
                      <tr
                        key={`${location.id}-${categoryIndex}`}
                        style={{ backgroundColor: categoryIndex % 2 === 0 ? "#fafbff" : "#fff", transition: "all 0.2s ease" }}
                      >
                        <td style={{ padding: "12px", textAlign: "center", fontWeight: "600", color: "#6b7280", borderBottom: "1px solid #e5e7eb" }}>
                          {categoryIndex + 1}
                        </td>
                        <td style={{ padding: "12px", fontWeight: "600", borderBottom: "1px solid #e5e7eb", background: category.bgColor }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <div style={{ width: "16px", height: "16px", backgroundColor: category.color, borderRadius: "50%", boxShadow: "0 2px 8px rgba(0,0,0,0.15)", border: "2px solid #fff" }}></div>
                            <span style={{ color: "#374151" }}>{category.name}</span>
                          </div>
                        </td>
                        <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #e5e7eb" }}>
                          <div style={{ padding: "8px 12px", backgroundColor: "#f8fafc", border: "2px solid #e2e8f0", borderRadius: "8px", fontSize: "13px", fontWeight: "500", color: "#475569" }}>
                            {getBiomedicalValue(location.id, categoryIndex, "min")}
                          </div>
                        </td>
                        <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #e5e7eb" }}>
                          <div style={{ padding: "8px 12px", backgroundColor: "#f8fafc", border: "2px solid #e2e8f0", borderRadius: "8px", fontSize: "13px", fontWeight: "500", color: "#475569" }}>
                            {getBiomedicalValue(location.id, categoryIndex, "max")}
                          </div>
                        </td>
                        <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #e5e7eb" }}>
                          <div style={{ padding: "8px 12px", backgroundColor: "#f0f9ff", border: "2px solid #0ea5e9", borderRadius: "8px", fontSize: "13px", fontWeight: "600", color: "#0369a1" }}>
                            {formatNum(getBiomedicalValue(location.id, categoryIndex, "actual"))}
                          </div>
                        </td>
                        <>
                          <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #e5e7eb" }}>
                            <div style={{
                              padding: "8px 12px",
                              backgroundColor: difference === "" ? "transparent" : (max == 0 || difference <= 0) ? '#f0fdf4' : "#fef2f2",
                              border: difference === "" ? "none" : (max == 0 || difference <= 0) ? '2px solid #86efac' : '2px solid #fca5a5',
                              borderRadius: "8px", fontSize: "13px", fontWeight: "700",
                              color: difference === "" ? "transparent" : (max == 0 || difference <= 0) ? '#16a34a' : "#dc2626"
                            }}>
                              {difference === "" ? "" : formatNum(difference)}
                            </div>
                          </td>
                          <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #e5e7eb" }}>
                            <div style={{
                              padding: "8px 12px",
                              backgroundColor: remark === "" ? "transparent" : remark === "Within Limit" ? "#ecfdf5" : "#fef2f2",
                              border: remark === "" ? "none" : remark === "Within Limit" ? "2px solid #22c55e" : "2px solid #ef4444",
                              borderRadius: "8px", fontSize: "13px", fontWeight: "700",
                              color: remark === "" ? "transparent" : remark === "Within Limit" ? "#15803d" : "#dc2626",
                            }}>
                              {remark === "Within Limit" ? "✅ Within Limit" : remark === "Exceeding Limit" ? "⚠️ Exceeding Limit" : ""}
                            </div>
                          </td>
                        </>
                      </tr>
                    );
                  })}     

                  {/* Total Waste Row */}
                  <tr style={{ backgroundColor: "#fff7ed", transition: "all 0.2s ease" }}>
                    <td style={{ padding: "12px", textAlign: "center", fontWeight: "600", color: "#6b7280", borderBottom: "1px solid #e5e7eb" }}>
                      {bioMedicalCategories.length + 1}
                    </td>
                    <td style={{ padding: "12px", fontWeight: "700", borderBottom: "1px solid #e5e7eb", background: "#fed7aa" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <div style={{ width: "16px", height: "16px", backgroundColor: "#f97316", borderRadius: "50%", boxShadow: "0 2px 8px rgba(0,0,0,0.15)", border: "2px solid #fff" }}></div>
                        <span style={{ color: "#9a3412" }}>Total Biomedical Waste</span>
                      </div>
                    </td>
                    <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #e5e7eb" }}>
                      <div style={{ padding: "8px 12px", backgroundColor: "#f8fafc", border: "2px solid #e2e8f0", borderRadius: "8px", fontSize: "13px", fontWeight: "700", color: "#475569" }}>
                        {formatNum(calculateTotalWaste(location.id, "min")) || "0"}
                      </div>
                    </td>
                    <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #e5e7eb" }}>
                      <div style={{ padding: "8px 12px", backgroundColor: "#f8fafc", border: "2px solid #e2e8f0", borderRadius: "8px", fontSize: "13px", fontWeight: "700", color: "#475569" }}>
                        {formatNum(calculateTotalWaste(location.id, "max"))}
                      </div>
                    </td>
                    <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #e5e7eb" }}>
                      <div style={{ padding: "8px 12px", backgroundColor: "#f0f9ff", border: "2px solid #0ea5e9", borderRadius: "8px", fontSize: "13px", fontWeight: "700", color: "#0369a1" }}>
                        {formatNum(calculateTotalWaste(location.id, "actual"))}
                      </div>
                    </td>
                    <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #e5e7eb" }}>
                      <div style={{
                        padding: "8px 12px",
                        backgroundColor: (() => { const d = calculateCalculatedDifference(calculateTotalWaste(location.id, "actual"), calculateTotalWaste(location.id, "max")); if (d === "" || d === "Submission Pending") return "transparent"; return (calculateTotalWaste(location.id, "max") == 0 || d <= 0) ? "#f0fdf4" : "#fef2f2"; })(),
                        border: (() => { const d = calculateCalculatedDifference(calculateTotalWaste(location.id, "actual"), calculateTotalWaste(location.id, "max")); if (d === "" || d === "Submission Pending") return "none"; return (calculateTotalWaste(location.id, "max") == 0 || d <= 0) ? "2px solid #86efac" : "2px solid #fca5a5"; })(),
                        borderRadius: "8px", fontSize: "13px", fontWeight: "700",
                        color: (() => { const d = calculateCalculatedDifference(calculateTotalWaste(location.id, "actual"), calculateTotalWaste(location.id, "max")); if (d === "" || d === "Submission Pending") return "transparent"; return (calculateTotalWaste(location.id, "max") == 0 || d <= 0) ? "#16a34a" : "#dc2626"; })(),
                      }}>
                        {(() => { const diff = calculateCalculatedDifference(calculateTotalWaste(location.id, "actual"), calculateTotalWaste(location.id, "max")); return diff === "" ? "" : typeof diff === "number" ? formatNum(diff) : diff; })()}
                      </div>
                    </td>
                    <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #e5e7eb" }}>
                      <div style={{
                        padding: "8px 12px",
                        backgroundColor: (() => { const act = calculateTotalWaste(location.id, "actual"); const max = calculateTotalWaste(location.id, "max"); if (act === "Submission Pending" || max === "Submission Pending" || act === "" || max === "") return "transparent"; const rem = parseInt(max) == 0 ? "Within Limit" : getCalculatedRemark(calculateCalculatedDifference(act, max)); return rem === "Within Limit" ? "#ecfdf5" : "#fef2f2"; })(),
                        border: (() => { const act = calculateTotalWaste(location.id, "actual"); const max = calculateTotalWaste(location.id, "max"); if (act === "Submission Pending" || max === "Submission Pending" || act === "" || max === "") return "none"; const rem = parseInt(max) == 0 ? "Within Limit" : getCalculatedRemark(calculateCalculatedDifference(act, max)); return rem === "Within Limit" ? "2px solid #22c55e" : "2px solid #ef4444"; })(),
                        borderRadius: "8px", fontSize: "13px", fontWeight: "700",
                        color: (() => { const act = calculateTotalWaste(location.id, "actual"); const max = calculateTotalWaste(location.id, "max"); if (act === "Submission Pending" || max === "Submission Pending" || act === "" || max === "") return "transparent"; const remC = parseInt(max) == 0 ? "Within Limit" : getCalculatedRemark(calculateCalculatedDifference(act, max)); return remC === "Within Limit" ? "#15803d" : "#dc2626"; })(),
                      }}>
                        {(() => { const act = calculateTotalWaste(location.id, "actual"); const max = calculateTotalWaste(location.id, "max"); if (act === "Submission Pending" || max === "Submission Pending" || act === "" || max === "") return ""; const rem = parseInt(max) == 0 ? "Within Limit" : getCalculatedRemark(calculateCalculatedDifference(act, max)); return rem === "Within Limit" ? "✅ Within Limit" : rem === "Exceeding Limit" ? "⚠️ Exceeding Limit" : ""; })()}
                      </div>
                    </td>
                  </tr>

                  {/* Per Bed Per Day Row — always shown when no intensity data (no approvedBeds guard) */}
                  {intensityData.length === 0 && (() => {
                    const perBedMax = calculatePerBedPerDay(location.id, approvedBeds);
                    const totalActual = calculateTotalWaste(location.id, "actual");
                    const days = getDaysInMonth(selectedMonth);
                    const beds = parseFloat(approvedBeds);

                    const perBedActual =
                      totalActual !== "Submission Pending" && !isNaN(beds) && beds > 0 && days
                        ? parseFloat(totalActual) / (beds * days)
                        : "Submission Pending";

                    const perBedDiff =
                      typeof perBedActual === "number" && typeof perBedMax === "number"
                        ? perBedActual - perBedMax
                        : null;

                    const perBedRemark =
                      perBedDiff === null
                        ? ""
                        : perBedMax == 0 || perBedDiff <= 0
                        ? "Within Limit"
                        : "Exceeding Limit";

                    return (
                      <tr style={{ backgroundColor: "#fef3c7", transition: "all 0.2s ease" }}>
                        <td style={{ padding: "12px", textAlign: "center", fontWeight: "600", color: "#6b7280", borderBottom: "1px solid #e5e7eb" }}>
                          {bioMedicalCategories.length + 2}
                        </td>
                        <td style={{ padding: "12px", fontWeight: "700", borderBottom: "1px solid #e5e7eb", background: "#fde68a" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <div style={{ width: "16px", height: "16px", backgroundColor: "#eab308", borderRadius: "50%", boxShadow: "0 2px 8px rgba(0,0,0,0.15)", border: "2px solid #fff" }}></div>
                            <span style={{ color: "#713f12" }}>Total Biomedical Waste Per Bed Per Day</span>
                          </div>
                        </td>
                        <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #e5e7eb" }}>
                          <div style={{ padding: "8px 12px", backgroundColor: "#f8fafc", border: "2px solid #e2e8f0", borderRadius: "8px", fontSize: "13px", fontWeight: "700", color: "#475569" }}>
                            0
                          </div>
                        </td>
                        <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #e5e7eb" }}>
                          <div style={{ padding: "8px 12px", backgroundColor: "#f8fafc", border: "2px solid #e2e8f0", borderRadius: "8px", fontSize: "13px", fontWeight: "700", color: "#475569" }}>
                            {formatNum(perBedMax)}
                          </div>
                        </td>
                        <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #e5e7eb" }}>
                          <div style={{ padding: "8px 12px", backgroundColor: "#f0f9ff", border: "2px solid #0ea5e9", borderRadius: "8px", fontSize: "13px", fontWeight: "700", color: "#0369a1" }}>
                            {typeof perBedActual === "number" ? formatNum(perBedActual) : perBedActual}
                          </div>
                        </td>
                        <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #e5e7eb" }}>
                          <div style={{
                            padding: "8px 12px",
                            backgroundColor: perBedDiff === null ? "transparent" : (perBedMax == 0 || perBedDiff <= 0) ? "#f0fdf4" : "#fef2f2",
                            border: perBedDiff === null ? "none" : (perBedMax == 0 || perBedDiff <= 0) ? "2px solid #86efac" : "2px solid #fca5a5",
                            borderRadius: "8px", fontSize: "13px", fontWeight: "700",
                            color: perBedDiff === null ? "transparent" : (perBedMax == 0 || perBedDiff <= 0) ? "#16a34a" : "#dc2626",
                          }}>
                            {perBedDiff === null ? "" : formatNum(perBedDiff)}
                          </div>
                        </td>
                        <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #e5e7eb" }}>
                          <div style={{
                            padding: "8px 12px",
                            backgroundColor: perBedDiff === null ? "transparent" : perBedRemark === "Within Limit" ? "#ecfdf5" : "#fef2f2",
                            border: perBedDiff === null ? "none" : perBedRemark === "Within Limit" ? "2px solid #22c55e" : "2px solid #ef4444",
                            borderRadius: "8px", fontSize: "13px", fontWeight: "700",
                            color: perBedDiff === null ? "transparent" : perBedRemark === "Within Limit" ? "#15803d" : "#dc2626",
                          }}>
                            {perBedDiff === null ? "" : perBedRemark === "Within Limit" ? "✅ Within Limit" : "⚠️ Exceeding Limit"}
                          </div>
                        </td>
                      </tr>
                    );
                  })()}

                  {/* Intensity Data Rows */}
                  {intensityData.map((intensity, intensityIndex) => {
                    const intensityActual = calculateIntensityMetric(location.id, intensity.answer, "actual");
                    const intensityMin = calculateIntensityMetric(location.id, intensity.answer, "min");
                    const perBedMax = calculatePerBedPerDay(location.id, approvedBeds);
                    const intensityDiff =
                      typeof intensityActual === "number" && typeof perBedMax === "number"
                        ? intensityActual - perBedMax
                        : null;
                    const intensityRemark =
                      intensityDiff === null
                        ? ""
                        : (perBedMax == 0 || intensityDiff <= 0)
                          ? "Within Limit"
                          : "Exceeding Limit";

                    return (
                      <tr
                        key={`${location.id}-intensity-${intensityIndex}`}
                        style={{ backgroundColor: "#fef3c7", transition: "all 0.2s ease" }}
                      >
                        <td style={{ padding: "12px", textAlign: "center", fontWeight: "600", color: "#6b7280", borderBottom: "1px solid #e5e7eb" }}>
                          {bioMedicalCategories.length + 2 + intensityIndex}
                        </td>
                        <td style={{ padding: "12px", fontWeight: "700", borderBottom: "1px solid #e5e7eb", background: "#fde68a" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <div style={{ width: "16px", height: "16px", backgroundColor: "#eab308", borderRadius: "50%", boxShadow: "0 2px 8px rgba(0,0,0,0.15)", border: "2px solid #fff" }}></div>
                            <span style={{ color: "#713f12" }}>Total Biomedical Waste Per {intensity.title} Per Day</span>
                          </div>
                        </td>
                        <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #e5e7eb" }}>
                          <div style={{ padding: "8px 12px", backgroundColor: "#f8fafc", border: "2px solid #e2e8f0", borderRadius: "8px", fontSize: "13px", fontWeight: "700", color: "#475569" }}>
                            {(intensityMin && typeof(intensityMin) === 'number') ? intensityMin : 0}
                          </div>
                        </td>
                        <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #e5e7eb" }}>
                          <div style={{ padding: "8px 12px", backgroundColor: "#f8fafc", border: "2px solid #e2e8f0", borderRadius: "8px", fontSize: "13px", fontWeight: "700", color: "#475569" }}>
                            {(() => { const value = calculatePerBedPerDay(location.id, approvedBeds); return formatNum(value); })()}
                          </div>
                        </td>
                        <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #e5e7eb" }}>
                          <div style={{ padding: "8px 12px", backgroundColor: "#f0f9ff", border: "2px solid #0ea5e9", borderRadius: "8px", fontSize: "13px", fontWeight: "700", color: "#0369a1" }}>
                            {typeof intensityActual === "number" ? formatNum(intensityActual) : intensityActual}
                          </div>
                        </td>
                        <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #e5e7eb" }}>
                          <div style={{
                            padding: "8px 12px",
                            backgroundColor: intensityDiff === null ? "transparent" : (perBedMax == 0 || intensityDiff <= 0) ? '#f0fdf4' : "#fef2f2",
                            border: intensityDiff === null ? "none" : (perBedMax == 0 || intensityDiff <= 0) ? "2px solid #86efac" : "2px solid #fca5a5",
                            borderRadius: "8px", fontSize: "13px", fontWeight: "700",
                            color: intensityDiff === null ? "transparent" : (perBedMax == 0 || intensityDiff <= 0) ? '#16a34a' : "#dc2626"
                          }}>
                            {intensityDiff === null ? "" : formatNum(intensityDiff)}
                          </div>
                        </td>
                        <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #e5e7eb" }}>
                          <div style={{
                            padding: "8px 12px",
                            backgroundColor: intensityDiff === null ? "transparent" : (perBedMax == 0 || intensityRemark === "Within Limit") ? "#ecfdf5" : "#fef2f2",
                            border: intensityDiff === null ? "none" : (perBedMax == 0 || intensityRemark === "Within Limit") ? "2px solid #22c55e" : "2px solid #ef4444",
                            borderRadius: "8px", fontSize: "13px", fontWeight: "700",
                            color: intensityDiff === null ? "transparent" : (perBedMax == 0 || intensityRemark === "Within Limit") ? "#15803d" : "#dc2626",
                          }}>
                            {intensityDiff === null ? "" : intensityRemark}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px", padding: "0 8px" }}>
        <div>
          <h3 style={{ margin: "0 0 8px 0", color: "#1f2937", fontSize: "24px", fontWeight: "700" }}>
            🧬 Bio Medical Waste Management
          </h3>
          <p style={{ margin: "0", color: "#6b7280", fontSize: "14px" }}>
            Monitor and track biomedical waste across all locations
          </p>
        </div>
        <button
          onClick={exportToExcel}
          style={{ padding: "12px 24px", background: "linear-gradient(135deg, #10b981 0%, #059669 100%)", color: "#fff", border: "none", borderRadius: "12px", cursor: "pointer", fontSize: "14px", fontWeight: "600", display: "flex", alignItems: "center", gap: "10px", boxShadow: "0 4px 16px rgba(16, 185, 129, 0.3)", transition: "all 0.2s ease" }}
          onMouseEnter={(e) => { e.target.style.transform = "translateY(-2px)"; e.target.style.boxShadow = "0 6px 20px rgba(16, 185, 129, 0.4)"; }}
          onMouseLeave={(e) => { e.target.style.transform = "translateY(0)"; e.target.style.boxShadow = "0 4px 16px rgba(16, 185, 129, 0.3)"; }}
        >
          <span>📊</span>
          Export to Excel
        </button>
      </div>
      {renderBioMedicalTable()}
    </div>
  );
};

export default BioMedicalTable;